import {
  flip,
  offset,
  shift,
  useClientPoint,
  useFloating,
} from '@floating-ui/react';
import {
  AxisUnit,
  CreateChartOptions,
  ResampleIntervals,
  TimeSeriesItem,
} from '@toggle/chart';
import { Entity, ExploreSingleInsightWithMeta } from '@toggle/toggle';
import { bisector } from 'd3';
import React, { RefObject, useCallback, useMemo } from 'react';

import { useChartApi } from '~/hooks/use-chart-api/useChartApi';
import { startOfDayUTC } from '~/modules/impacted-companies/components/utils';
import { TooltipSortConfig } from '~/modules/multi-line-chart/MultiLineChartModule';
import { SimpleChartTooltip } from '~/modules/simple-chart/components/simple-chart-tooltip/SimpleChartTooltip';
import { useGutterSize } from '~/modules/turbo-chart/hooks/use-gutter-size/useGutterSize';
import { useLegendPriceHover } from '~/views/turbo-chart/hooks/use-legend-price-hover/useLegendPriceHover';
import { UseTurboChartReturn } from '~/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import type {
  TerminalChartAssetData,
  TerminalPaneData,
} from '~/views/turbo-chart/utils/chart-utils/types';

import { ChartInsightIcons } from '../chart-insight-icons/ChartInsightIcons';
import styles from './PriceChartArea.module.scss';

const TIME_SUFFIX = 'T00:00:00Z';

export interface PriceChartAreaProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  enablePriceHover?: boolean;
  options: CreateChartOptions;
  chartState: UseTurboChartReturn['chartState'];
  primaryAsset: TerminalChartAssetData;
  primaryPane: TerminalPaneData;
  tooltipSortConfig?: TooltipSortConfig;
  axisUnit?: string;
  selectedInsights: ExploreSingleInsightWithMeta[];
  activeInsightId: string;
  dailyTimeseries?: TimeSeriesItem[];
  handleSetActiveInsightId: (id: string) => void;
  entity: Entity;
}

export const PriceChartArea = ({
  canvasRef,
  enablePriceHover = true,
  options,
  chartState,
  primaryAsset,
  primaryPane,
  tooltipSortConfig,
  axisUnit,
  selectedInsights,
  activeInsightId,
  dailyTimeseries,
  handleSetActiveInsightId,
  entity,
}: PriceChartAreaProps) => {
  const { gutterSize } = useGutterSize({
    canvasRef,
  });
  const chartApiRef = useChartApi({
    canvasRef,
    options,
    chartState,
    primaryAsset,
    primaryPane,
  });

  const priceHover = useLegendPriceHover({
    elementRef: canvasRef,
    enabled: enablePriceHover,
  });

  const { refs, floatingStyles, context } = useFloating({
    open: !!priceHover,
    placement: 'top',
    strategy: 'fixed',
    middleware: [offset(15), flip(), shift({ padding: 5 })],
  });
  useClientPoint(context, {
    x: priceHover?.x,
    y: priceHover?.y,
  });

  const refCallback = (node: HTMLCanvasElement) => {
    canvasRef.current = node;
    refs.setReference(node);
  };

  const panes = gutterSize.panes;
  const domain = chartApiRef.current?.getBaseData()?.domain;

  const getActualIndexOnChart = useCallback(
    ({ insightDate }: { insightDate: Date }) => {
      let timeSeries = dailyTimeseries;

      if (!timeSeries || !domain) {
        return undefined;
      }

      if (
        [ResampleIntervals.OneHour, ResampleIntervals.FiveMinutes].includes(
          chartState.resample
        )
      ) {
        timeSeries = primaryAsset.originalTs;
      }

      const bis = bisector((d: TimeSeriesItem) => {
        if (!d) {
          return new Date();
        }

        return new Date(d.time);
      });
      const otherScaleIndex = bis.left(
        timeSeries,
        new Date(insightDate),
        domain[0],
        domain[1]
      );

      const isoDate = `${insightDate.toISOString().slice(0, 10)}${TIME_SUFFIX}`;

      const foundIndex = timeSeries.findIndex(item => item.time === isoDate);
      const dailyScaleIndex = foundIndex === -1 ? undefined : foundIndex;

      switch (chartState.resample) {
        case ResampleIntervals.OneDay:
          return dailyScaleIndex;
        case ResampleIntervals.OneHour:
        case ResampleIntervals.FiveMinutes:
          return otherScaleIndex;
        default:
          return dailyScaleIndex;
      }
    },
    [chartState.resample, dailyTimeseries, domain, primaryAsset.originalTs]
  );
  const activeInsightIndex = useMemo(() => {
    const activeInsight = selectedInsights.find(
      insight => insight.id === activeInsightId
    );
    const insightDate = activeInsight?.created_at;

    if (!dailyTimeseries || !domain || !insightDate) {
      return undefined;
    }

    return getActualIndexOnChart({
      insightDate: startOfDayUTC(new Date(insightDate)),
    });
  }, [
    activeInsightId,
    dailyTimeseries,
    domain,
    getActualIndexOnChart,
    selectedInsights,
  ]);

  const chartWidth = options.width;
  const gutteryY = gutterSize.y;
  const getRightPositionOnChart = useCallback(
    ({ activeIndex }: { activeIndex?: number }) => {
      if (!domain || activeIndex === undefined || !dailyTimeseries) {
        return undefined;
      }

      const startIndex = domain[0];
      const endIndex = domain[1];

      const domainLength = endIndex - startIndex;
      const activeIndexLength = endIndex - activeIndex;
      const actualChartWidth = chartWidth - gutteryY;
      const rightPosition =
        (activeIndexLength * actualChartWidth) / domainLength + gutteryY;

      return rightPosition;
    },
    [chartWidth, dailyTimeseries, domain, gutteryY]
  );

  const dottedLineRight = getRightPositionOnChart({
    activeIndex: activeInsightIndex,
  });

  const insightsWithRightPos = useMemo(() => {
    const usedIndexes = new Map<number, number>();
    return selectedInsights.map(insight => {
      if (!dailyTimeseries || !domain) {
        return {
          ...insight,
          right: undefined,
          yOffsetCount: 0,
        };
      }

      const activeIndex = getActualIndexOnChart({
        insightDate: startOfDayUTC(new Date(insight.created_at)),
      });

      if (activeIndex !== undefined) {
        const usedIndex = usedIndexes.get(activeIndex);
        usedIndexes.set(activeIndex, (usedIndex ?? -1) + 1);
      }

      const yOffsetCount =
        activeIndex !== undefined ? usedIndexes.get(activeIndex) ?? 0 : 0;

      const right = getRightPositionOnChart({
        activeIndex,
      });

      return {
        ...insight,
        right,
        yOffsetCount,
      };
    });
  }, [
    dailyTimeseries,
    domain,
    getActualIndexOnChart,
    getRightPositionOnChart,
    selectedInsights,
  ]);

  return (
    <>
      <ChartInsightIcons
        articleInsights={insightsWithRightPos}
        handleSetActiveInsightId={handleSetActiveInsightId}
        entity={entity}
      />
      {dottedLineRight && (
        <div
          className={styles.dateDottedLine}
          data-testid="date-dotted-line"
          style={{ '--right': `${dottedLineRight}px` } as React.CSSProperties}
        />
      )}

      {context.open && (
        <SimpleChartTooltip
          ref={refs.setFloating}
          styles={floatingStyles}
          priceHover={priceHover}
          sortConfig={tooltipSortConfig}
        />
      )}
      <canvas
        data-testid="chart-area-canvas"
        ref={refCallback}
        style={{ width: options.width, height: options.height }}
      />
      {!!panes.length && axisUnit && (
        <AxisUnit
          top={-10}
          label={axisUnit}
          isMixed={false}
          tooltipLabel=""
          offset={gutterSize.y - gutterSize.yAxisSizes[0].width}
          gutterWidth={gutterSize.yAxisSizes[0].width}
        />
      )}
    </>
  );
};
